// `grid calls` — what an app of yours calls (Decision 051).
//
// The caller side of App Connection. `list` shows the outbound grants an
// app holds — what it may call, what's still pending the other owner's
// approval, and what was revoked. `withdraw` drops the app's own request or
// access (DELETE on the outbound route; the callee admin's `grid callers
// revoke` is the other side of the same coin).

import { Command } from 'commander';
import type { CallerGrant } from '../../../../shared/src/v1-types.js';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { CloudGridError } from '../../utils.js';
import {
  fetchEntityNames,
  lookupCallEntity,
  matchGrants,
  renderGrantsTable,
  resolveAppTarget,
  serializeGrant,
  sortGrants,
  counterpartId,
} from './shared.js';

interface ListOpts {
  json?: boolean;
  output?: string;
}

/** Load the app (caller) and its outbound grants + the grid's name map. */
async function loadOutbound(
  client: IV2Client,
  appArg: string | undefined,
): Promise<{
  app: { entity_id: string; name: string };
  grants: CallerGrant[];
  names: Map<string, string>;
}> {
  const app = await lookupCallEntity(client, resolveAppTarget(appArg));
  const [{ grants }, names] = await Promise.all([
    client.get<{ grants: CallerGrant[] }>(
      `/api/v2/entities/${encodeURIComponent(app.entity_id)}/calling-grants`,
    ),
    fetchEntityNames(client),
  ]);
  return { app: { entity_id: app.entity_id, name: app.name }, grants, names };
}

// ── list ──────────────────────────────────────────────────────────────────

export async function runCallsList(
  client: IV2Client,
  appArg: string | undefined,
  opts: ListOpts,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const { app, grants, names } = await loadOutbound(client, appArg);
  const rows = sortGrants(grants, names, 'outbound');

  out.json(() => ({
    app,
    total: rows.length,
    grants: rows.map((g) => serializeGrant(g, names, 'outbound')),
  }));

  out.human(() => {
    if (rows.length === 0) {
      return `${app.name} doesn't call other apps. Declare calls: in its cloudgrid.yaml and plug to request access.`;
    }
    return renderGrantsTable(rows, names, 'outbound', out.isWide);
  });
}

// ── withdraw ──────────────────────────────────────────────────────────────

export async function runCallsWithdraw(
  client: IV2Client,
  appArg: string,
  targetArg: string,
  opts: { json?: boolean },
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { app, grants, names } = await loadOutbound(client, appArg);
  const matched = matchGrants(grants, targetArg, names, 'outbound');
  if (matched.length === 0) {
    throw new CloudGridError(
      `${app.name} doesn't call ${targetArg}. Run grid calls list ${app.name} to see its outbound grants.`,
    );
  }
  // Prefer the live grant; the server echoes terminal grants idempotently.
  const grantToDrop =
    matched.find((g) => g.status === 'approved') ??
    matched.find((g) => g.status === 'requested') ??
    matched[0];
  const targetName = names.get(counterpartId(grantToDrop, 'outbound')) ?? targetArg;

  const { grant } = await client.del<{ grant: CallerGrant }>(
    `/api/v2/entities/${encodeURIComponent(app.entity_id)}/calling-grants/${encodeURIComponent(grantToDrop.grant_id)}`,
  );

  out.json(() => ({ grant: serializeGrant(grant, names, 'outbound') }));
  out.human(() =>
    `Withdrawn. ${app.name} no longer calls ${targetName}. If cloudgrid.yaml still declares calls: ${targetName}, the next plug requests access again.`,
  );
}

// ── Commander wiring ──────────────────────────────────────────────────────

const listCommand = addOutputOption(
  new Command('list')
    .description('Show what an app of yours calls, and what is still pending')
    .argument('[app]', 'App address (defaults to the entity linked to this folder)')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (app: string | undefined, opts: ListOpts) => {
  try {
    const client = await getV2Client();
    await runCallsList(client, app, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

const withdrawCommand = new Command('withdraw')
  .description("Withdraw an app's request or access to another app")
  .argument('<app>', 'The calling app (an app you develop)')
  .argument('<target>', 'Target address or grant id')
  .option('--json', 'machine-readable output')
  .action(async (app: string, target: string, opts: { json?: boolean }) => {
    try {
      const client = await getV2Client();
      await runCallsWithdraw(client, app, target, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

export const callsCommand = new Command('calls')
  .description('What an app of yours calls: list, withdraw')
  .addCommand(listCommand)
  .addCommand(withdrawCommand);
